import { FONT, T, UI } from "../constants/tokens";
import { fmtPct } from "../utils/formatters";
import { mainSentence, extendedSentence, briefTitle } from "../utils/brief";
import { dayLabel, sessionWeekday, israelClock } from "../utils/marketStatus";
import { EXTENDED_INFO } from "../utils/extendedHours";
import DualMoney from "./DualMoney";
import Money from "./Money";
import HeroChange from "./HeroChange";
import EmptyState from "./EmptyState";
import InfoTip from "./InfoTip";
import LTR from "./LTR";
import { Skeleton as Skel, Loading } from "./Skeleton";

// ─── "הבוקר בקצרה" / "היום בקצרה" (sprint 19, stage 2) ─────────────────────
// The top of the home screen: what the portfolio did in the last session, in
// one or two sentences built in code from fixed templates (utils/brief.js),
// and under them the value, the day's change and the extended-hours line.
// It replaces the value card and its landscape. Dark in both themes
// (.si-brief, index.css): it is the screen's one focus.

const tone = d => (d > 0 ? UI.gain : d < 0 ? UI.loss : UI.text.primary);

// One sentence: Hebrew text, figures and symbols isolated LTR.
function Sentence({ segs, style, after = null }) {
  if (!segs.length) return null;
  return (
    <p style={{ margin: 0, ...style }}>
      {segs.map((s, i) => typeof s === "string" ? <span key={i}>{s}</span>
        : s.symbol ? <LTR key={i} style={{ fontFamily: FONT.latin, fontWeight: 800 }}>{s.symbol}</LTR>
        : s.latin ? <LTR key={i}>{s.latin}</LTR>
        : <LTR key={i} className="si-num" style={{ fontWeight: 800, color: tone(s.tone) }}>{fmtPct(s.pct, { signed: s.signed })}</LTR>)}
      {after && <> {after}</>}
    </p>
  );
}

// What the extended-hours sentence rests on, for its ⓘ: how many holdings
// traded, when the last trade and the futures quote are from, the method and
// the source (sprint 19 wording round · these were a line of their own under
// the value, repeating the sentence).
function extendedDetail(est, future) {
  const parts = [EXTENDED_INFO + "."];
  if (est) {
    parts.push(`התיק: ${est.traded} מתוך ${est.total} החזקות נסחרו במסחר המורחב, עסקה אחרונה ${israelClock(est.time)} שעון ישראל.`);
    parts.push("ההערכה: סכום השינוי של כל החזקה במסחר המורחב כפול משקלה בתיק, לפי העסקה האחרונה של כל החזקה. החזקה שלא נסחרה נספרת במחיר הסגירה שלה.");
    // The largest parts of the sum, so it can be checked (sprint 21, 4c).
    const top = (est.contributions || []).filter(c => Math.abs(c.points) >= 0.005).slice(0, 4);
    // The weight is within the holdings in the estimate (those Yahoo answered for).
    if (top.length) parts.push(`התרומות הגדולות (משקל מתוך ההחזקות שבהערכה): ${top.map(c => `\u2066${c.symbol} ${fmtPct(c.points).replace("%", "")}\u2069 נק׳ (משקל \u2066${fmtPct(c.weight, { decimals: 1, signed: false })}\u2069, \u2066${fmtPct(c.pct)}\u2069)`).join(" · ")}.`);
  }
  if (future) parts.push(`חוזי S&P 500: מחיר ${israelClock(future.time)} שעון ישראל, מול הסגירה הקודמת.`);
  parts.push("מקור: Yahoo Finance.");
  return parts.join(" ");
}

// Every source behind the card, for the footer's ⓘ.
function sourcesDetail(index, extendedShown) {
  return [
    "שווי התיק, השינוי היומי והתרומה של כל החזקה: מחירים מ-Finnhub.",
    "הסכום בשקלים: שער דולר/שקל מ-Yahoo Finance.",
    index ? `S&P 500 באותו יום: ${index.source}.` : null,
    extendedShown ? "מסחר מורחב וחוזים: Yahoo Finance." : null,
    "המשפטים נבנים בקוד מהמספרים, לפי תבניות קבועות, בלי AI.",
  ].filter(Boolean).join(" ");
}

export default function BriefCard({ portfolio, market, movers, index, extended, money, cur, loading, isDesktop }) {
  const now = new Date();
  const main = movers?.total ? mainSentence({
    total: movers.total, leader: movers.rows[0] || null,
    open: market.open, today: market.lastSession === market.date, weekday: sessionWeekday(market), index,
  }) : [];
  const ext = extended ? extendedSentence({ state: market.state, estimate: extended.estimate, future: market.state === "pre" ? extended.future : null }) : [];
  const est = extended?.estimate || null;
  const fut = market.state === "pre" ? extended?.future || null : null;
  const sources = [...new Set(["Finnhub", "Yahoo Finance", index?.source].filter(Boolean))];

  return (
    <section className="si-brief" data-brief style={{ borderRadius: T.radius.xxl, padding: isDesktop ? "22px 24px" : "18px 18px 16px" }}>
      <h2 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: UI.text.secondary, fontFamily: FONT.heading }}>{briefTitle(now)}</h2>

      {loading ? (
        <Loading style={{ marginTop: 12 }}><Skel w="90%" h={18} mb={10}/><Skel w="70%" h={18} mb={18}/><Skel w="45%" h={30}/></Loading>
      ) : !portfolio.length ? (
        <div style={{ marginTop: 8 }}>
          <EmptyState icon="wallet" line="אין החזקות בתיק עדיין" sub="הוספת נייר ראשון נעשית במסך התיק"/>
        </div>
      ) : (
        <>
          {main.length ? (
            <Sentence segs={main} style={{ marginTop: 8, fontSize: isDesktop ? 20 : 18, fontWeight: 700, lineHeight: 1.55, color: UI.text.primary }}/>
          ) : (
            <p style={{ margin: "8px 0 0", fontSize: 15, color: UI.text.secondary }}>אין עדיין נתוני שינוי יומי להחזקות</p>
          )}
          <Sentence segs={ext} style={{ marginTop: 8, fontSize: 15, lineHeight: 1.6, color: UI.text.secondary }}
            after={<InfoTip label="על המסחר המורחב" size={13} text={extendedDetail(est, fut)}/>}/>

          {/* The figures: value and the last session's change. */}
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${UI.line.hairline}`, display: "flex", flexDirection: "column", gap: 10 }}>
            <div>
              <div style={{ fontSize: 12.5, color: UI.text.muted, marginBottom: 2 }}>שווי התיק</div>
              <DualMoney usd={money.value} cur={cur} size={isDesktop ? 30 : 26}>
                <Money value={money.value} cur={cur} size={isDesktop ? 30 : 26} weight={700} className="si-num" whole
                  color={UI.text.primary} style={{ lineHeight: 1.1, letterSpacing: "-0.02em" }}/>
              </DualMoney>
            </div>
            {money.day && <HeroChange cur={cur} dollar={money.day.dollar} pct={money.day.pct} label={dayLabel(market)} size={14}/>}
            {money.unquoted > 0 && (
              <div style={{ fontSize: 12, color: UI.text.muted }}>
                {money.unquoted === 1 ? "החזקה אחת ללא ציטוט אינה כלולה בסכום" : <><LTR className="si-num">{money.unquoted}</LTR> החזקות ללא ציטוט אינן כלולות בסכום</>}
              </div>
            )}
          </div>

          <p style={{ margin: "12px 0 0", fontSize: 11.5, color: UI.text.muted, lineHeight: 1.6 }}>
            מקורות: {sources.map((n, i) => <span key={n}>{i ? ", " : ""}<LTR>{n}</LTR></span>)}
            {" · "}המשפטים נבנים מהמספרים, בלי <LTR>AI</LTR>{" "}
            <InfoTip label="מה מגיע מאיפה" size={13} text={sourcesDetail(index, ext.length > 0)}/>
          </p>
        </>
      )}
    </section>
  );
}
